/*! Vue/DOM adaptation of clayharmon/webgl-liquid-glass (MIT). See LICENSE. */
import { createSpring, updateSpring } from './spring';

const snap = (spring, value = spring.target) => Object.assign(spring, {current:value, target:value, velocity:0});

export function createLiquidNavigation(nav, { getActive, select }) {
  const pill = nav.querySelector('.liquid-tabbar-pill');
  const clip = nav.querySelector('clipPath rect');
  const buttons = () => Array.from(nav.querySelectorAll('button'));
  const x = createSpring(0), width = createSpring(0), bulge = createSpring(0), skew = createSpring(0);
  const springs = [x, width, bulge, skew];
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let frame = 0, lastTime = 0, initialized = false, destroyed = false;
  let drag = null, lastPointerUp = -Infinity, visible = false;

  function wake() {
    if (!destroyed && visible && !document.hidden && !frame) {
      lastTime = performance.now();
      frame = requestAnimationFrame(draw);
    }
  }
  function target(index) {
    const button = buttons()[index];
    if (!button || !visible) return;
    x.target = button.offsetLeft + button.offsetWidth / 2;
    width.target = button.offsetWidth;
    if (!initialized) { snap(x); snap(width); initialized = true; }
    wake();
  }
  function draw(now) {
    frame = 0;
    if (destroyed || !visible || document.hidden) return;
    const dt = Math.min((now - lastTime) / 1000, .064);
    lastTime = now;
    if (reduced.matches) { snap(bulge, 0); snap(skew, 0); }
    else {
      updateSpring(bulge, dt, 260, 16);
      skew.target = drag?.moved ? drag.velocity * .6 : 0;
      updateSpring(skew, dt, 150, 7);
    }
    if (drag?.moved) {
      x.current = drag.x; x.velocity = reduced.matches ? 0 : drag.velocity * 30;
      width.current = width.target;
      drag.velocity *= .8;
    } else if (reduced.matches) { snap(x); snap(width); }
    else { updateSpring(x, dt); updateSpring(width, dt); }
    const sx = 1 + bulge.current * .12 + Math.min(Math.abs(skew.current) * .005, .2);
    const sy = 1 + bulge.current * .42 - Math.min(Math.abs(skew.current) * .003, .12);
    const angle = Math.max(-14, Math.min(14, skew.current * .35));
    const left = x.current - width.current / 2;
    pill.style.width = `${width.current}px`;
    pill.style.transform = `translateX(${left}px) scale(${sx},${sy}) skewX(${angle}deg)`;
    clip.setAttribute('width', Math.max(0, width.current));
    clip.setAttribute('transform', `translate(${left} 0) translate(${width.current / 2} 32) scale(${sx} ${sy}) skewX(${angle}) translate(${-width.current / 2} -32)`);
    // A held, motionless pointer does not need a continuous render loop.
    const moving = springs.some(s => Math.abs(s.current - s.target) > .001 || Math.abs(s.velocity) > .001);
    if (drag?.moved ? Math.abs(drag.velocity) > .01 || Math.abs(skew.current) > .01 : moving) frame = requestAnimationFrame(draw);
  }
  function cancel() {
    if (!drag) return;
    const id = drag.id;
    drag = null;
    bulge.target = 0; skew.target = 0;
    if (nav.hasPointerCapture(id)) nav.releasePointerCapture(id);
    target(getActive());
  }
  function measure() {
    cancel();
    visible = nav.offsetWidth > 0;
    if (!visible) { cancelAnimationFrame(frame); frame = 0; return; }
    nav.dataset.renderer = 'css';
    target(getActive());
  }
  function down(event) {
    if (!event.isPrimary || event.button !== 0 || drag) return;
    const button = event.target.closest('button');
    if (!button || !nav.contains(button)) return;
    const index = buttons().indexOf(button);
    const rect = nav.getBoundingClientRect();
    target(index);
    drag = {id:event.pointerId, startX:event.clientX, startY:event.clientY,
      prevX:event.clientX, x:x.target, startPill:x.target, moved:false, index, rect, velocity:0};
    nav.setPointerCapture(event.pointerId);
    bulge.target = 1;
    wake();
  }
  function move(event) {
    if (drag && event.pointerId === drag.id) {
      const dx = event.clientX - drag.startX, dy = event.clientY - drag.startY;
      if (!drag.moved && Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { cancel(); return; }
      if (Math.abs(dx) > 6) drag.moved = true;
      if (drag.moved) drag.x = Math.max(width.target / 2 + 8, Math.min(nav.clientWidth - width.target / 2 - 8, drag.startPill + dx));
      drag.velocity = drag.velocity * .75 + (event.clientX - drag.prevX) * .25;
      drag.prevX = event.clientX;
    }
    wake();
  }
  function up(event) {
    if (!drag || event.pointerId !== drag.id) return;
    if (event.clientY < drag.rect.top - 24 || event.clientY > drag.rect.bottom + 24) { cancel(); return; }
    let index = drag.index;
    if (drag.moved) {
      const centers = buttons().map(b => b.offsetLeft + b.offsetWidth / 2);
      index = centers.reduce((best, center, i) => Math.abs(center - drag.x) < Math.abs(centers[best] - drag.x) ? i : best, 0);
    }
    lastPointerUp = event.timeStamp;
    cancel();
    // The route is authoritative, including redirects or blocked navigation.
    select(index);
    target(getActive());
  }
  function click(event) {
    if (event.detail && event.timeStamp - lastPointerUp < 500) return;
    const button = event.target.closest('button');
    if (button && nav.contains(button)) select(buttons().indexOf(button));
  }
  function key(event) {
    if (event.key === 'Escape') { cancel(); return; }
    const items = buttons(), index = items.indexOf(event.target);
    if (index < 0) return;
    const next = {ArrowRight:(index + 1) % items.length, ArrowLeft:(index + items.length - 1) % items.length, Home:0, End:items.length - 1}[event.key];
    if (next === undefined) return;
    event.preventDefault(); items[next].focus();
  }
  function visibility() { if (document.hidden) { cancel(); cancelAnimationFrame(frame); frame = 0; } else measure(); }
  function sync() { if (!drag) target(getActive()); wake(); }
  const events = {pointerdown:down, pointermove:move, pointerup:up, pointercancel:cancel, lostpointercapture:cancel, click, keydown:key};
  Object.entries(events).forEach(([name, handler]) => nav.addEventListener(name, handler));
  const observer = new ResizeObserver(measure);
  observer.observe(nav);
  reduced.addEventListener('change', sync);
  window.addEventListener('resize', measure);
  document.addEventListener('visibilitychange', visibility);
  measure();
  return {sync, destroy() {
    destroyed = true; cancel(); cancelAnimationFrame(frame); observer.disconnect();
    Object.entries(events).forEach(([name, handler]) => nav.removeEventListener(name, handler));
    reduced.removeEventListener('change', sync);
    window.removeEventListener('resize', measure);
    document.removeEventListener('visibilitychange', visibility);
  }};
}
